// @flow

import _ from 'lodash';

import type {
  DataSet,
  NodeDefinition,
  NodeOptions,
  SelectedNode,
} from 'app/developer-v3/components/PartnerSupportForm/utils/tree/types';
import type { StringMap } from 'app/common/types';

const RE_ID = /^[a-z]+$/;
const RE_KEY_PATH = /^[a-z]+(?:\.[a-z]+)*$/;
const RE_KEY_PATH_VAL = /^[a-z]+(?:\.[a-z]+)*(?:\[[a-z]+\])+$/;

const LEGACY_STEP_KEYS = {
  zaps: 'zap',
  unserinput: 'help',
};

// FYI: I'd much rather have classes here and use this, but:
// https://github.com/zapier/frontend-guide/tree/master/javascript#classes
export const base = (options: NodeOptions): NodeDefinition => {
  if (!RE_ID.test(options.id)) {
    throw new Error(`ID must be a-z. Received: ${options.id}`);
  }

  const node = {
    id: options.id,
    isRequired: options.isRequired !== false,

    // you SHOULD override this to say if the data is valid
    // will ONLY be called if hasData() === true
    isDataValid:
      options.isDataValid ||
      (() => {
        return true;
      }),

    children: options.children || {},

    // override getNextNode() to select the next node from node.children
    // you can assume canNodeContinue === true which means there's no or valid data
    // all possible children should be referenced under node.children
    // this is to test the full tree, at the cost of memory and performance :(
    getNextNode:
      options.getNextNode ||
      (() => {
        const keys = Object.keys(node.children); // Flow can't type Object.values()
        if (keys.length === 1) {
          return node.children[keys[0]];
        }
        return null;
      }),

    // override to return React component
    render:
      options.render ||
      (() => {
        return null;
      }),

    // you'll probably want to override this to add the optional label and formatted prop
    // or return null or undefined to not export
    export:
      options.export ||
      (data => ({
        id: node.id,
        data,
      })),

    // you can override this to normalize data (e.g. when coming form prefill)
    // set data to null or undefined to indicate there is no data
    // when normalizing data make sure you don't mutate acceptable data and cause loops
    parseData: options.parseData || (data => data),

    // can be overriden if anything else (e.g. []) counts as having no data
    hasData:
      options.hasData ||
      (data => {
        return data !== undefined && data !== null;
      }),
  };
  return node;
};

// Takes query as it could come in from Query String and moves [..] under value object (see myData)
export const queryToDataSet = (query: StringMap): DataSet =>
  Object.entries(query).reduce((data, [key, value]) => {
    if (RE_KEY_PATH_VAL.test(key)) {
      const pos = key.indexOf('[');
      const stepKey = key.substring(0, pos);
      const nonLegacyStepKey = LEGACY_STEP_KEYS[stepKey] || stepKey;
      if (stepKey === nonLegacyStepKey || !data[nonLegacyStepKey]) {
        data[nonLegacyStepKey] = _.isPlainObject(data[nonLegacyStepKey])
          ? data[nonLegacyStepKey]
          : {};
        _.setWith(
          data,
          `['${nonLegacyStepKey}']${key.substr(pos)}`,
          value,
          Object
        ); // Object prevents Arrays
      }
    } else if (RE_KEY_PATH.test(key)) {
      const nonLegacyStepKey = LEGACY_STEP_KEYS[key] || key;
      if (key === nonLegacyStepKey || !data[nonLegacyStepKey]) {
        data[nonLegacyStepKey] = value;
      }
    }
    return data;
  }, {});

export const selectNodesInPath = (
  root: NodeDefinition,
  meta: DataSet,
  dataSet: DataSet
): Array<SelectedNode> => {
  const path = [];

  const addNodeToPath = (node: NodeDefinition): void => {
    const pathId = path
      .map(({ id }) => id)
      .concat([node.id])
      .join('.');
    const data = node.parseData(
      pathId in dataSet ? dataSet[pathId] : dataSet[node.id]
    );

    const nodeProps = {
      id: node.id,
      pathId,
      isCurrent: true,
      data,
      // If it has data, it has to be valid, else we can continue if not required
      canContinue: node.hasData(data)
        ? node.isDataValid(data)
        : !node.isRequired,
      export: () => (node.hasData(data) ? node.export(data) : null),
      render: node.render,
    };

    // more readable than a long if-statement
    const nextNode = (() => {
      if (!node.hasData(data)) {
        return null;
      }

      if (!nodeProps.canContinue) {
        return null;
      }

      return node.getNextNode(data, meta);
    })();

    if (nextNode) {
      nodeProps.isCurrent = false;
    }

    const selectedNode = {
      ...nodeProps,
    };

    path.push(selectedNode);

    if (nextNode) {
      addNodeToPath(nextNode);
    }
  };

  addNodeToPath(root);

  return path;
};
